
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { AxiosResponse } from 'axios';
import { mixins } from 'vue-class-component';
import CustomerGroup from '@/mixins/CustomerGroup';
import Editable from '@/mixins/Editable';
import StackedForm from '@/mixins/StackedForm';
import VWrapper from '@/components/shared/VWrapper.vue';
import ScreensaverForm from '@/components/screensaver/ScreensaverForm.vue';
import ScreensaverUpsellForm from '@/components/screensaver/ScreensaverUpsellForm.vue';
import ScreensaverStepTab from '@/components/screensaver/ScreensaverStepTab.vue';
import EditScreensaverLoader from '@/components/screensaver/EditScreensaverLoader.vue';
import Screensaver from '@/interfaces/models/Screensaver';
import { Venue } from '@/interfaces/models/Venue';
import { User } from '@/interfaces/models/User';
import { Article as ArticleModel } from '@/interfaces/models/Article';
import { CustomerGroup as CustomerGroupEnum } from '@/enums/CustomerGroup';
import FoodcardApiService from '@/api/http/FoodcardApiService';

const screensaver = namespace('screensaver');
const app = namespace('app');
const auth = namespace('auth');

@Component({
  components: { VWrapper, ScreensaverForm, ScreensaverUpsellForm, ScreensaverStepTab, EditScreensaverLoader },
})
export default class EditScreensaver extends mixins(CustomerGroup, Editable, StackedForm) {
  @screensaver.State('items') public screensavers!: Screensaver[];
  @screensaver.State('active') public activeScreensaver!: Screensaver;
  @screensaver.Action('fetch') public getScreensavers!: any;
  @screensaver.Action('store') public storeScreensaver!: any;
  @screensaver.Action('show') public getScreensaver!: any;
  @screensaver.Action('update') public updateScreensaver!: any;

  @app.State('venues') public venues!: Venue[];
  @app.Action('fetchVenues') public getVenues!: any;

  @auth.State('user') public user!: User;

  public activeTab = 0;

  public $refs!: {
    form: InstanceType<typeof ScreensaverForm> & { getData: () => any; validate: () => Promise<boolean | boolean[]> };
    upsellForm: InstanceType<typeof ScreensaverUpsellForm> & {
      getData: () => any;
      validate: () => Promise<boolean | boolean[]>;
    };
  };

  public customerGroup: string = '';
  public venuesByGroup: Venue[] = [];
  public articles: Array<Partial<ArticleModel>> = [];

  get screensaver() {
    if (this.activeScreensaver && this.editing) {
      return this.activeScreensaver;
    }

    return null;
  }

  get venuesSorted(): Venue[] {
    return this.venuesByGroup;
  }

  public async backToTable() {
    await this.$router.push({
      name: 'screensaver.index',
      query: {
        customerGroup: this.editing ? this.activeScreensaver.customerGroup : null,
      },
    });
  }

  public async created() {
    if (this.editing) {
      this.$startLoading('screensaver');
      await this.getScreensaver({ id: this.id });
      this.$stopLoading('screensaver');
    }

    this.$startLoading('venues');
    await this.getVenues();
    this.$stopLoading('venues');

    this.venuesByGroup = this.venues;

    await this.onCustomerGroupChange({
      customerGroup: this.editing ? this.screensaver?.customerGroup : undefined,
      venues: this.editing ? this.screensaver?.venues : [],
    });
  }

  public async onVenuesChange(data: { venues?: string[]; customerGroup?: CustomerGroupEnum }) {
    const foodcardApi: FoodcardApiService = new FoodcardApiService();
    const venues: Venue[] = !data.venues?.length
      ? this.venuesByGroup
      : this.venuesByGroup.filter((venue: Venue) => (data.venues || []).includes(venue._id));
    const venuesIds: string[] = venues.map((venue: Venue) => venue._id);

    let articles: Array<Partial<ArticleModel>> = [];

    this.$startLoading('article');

    for (const id of venuesIds) {
      const res: AxiosResponse<Array<Partial<ArticleModel>>> = await foodcardApi.getArticleNames({
        venue: id,
      });
      articles = [...articles, ...res.data];
    }

    this.articles = articles;

    this.$stopLoading('article');
  }

  public async onCustomerGroupChange(data: { venues?: string[]; customerGroup?: CustomerGroupEnum }) {
    const customerGroup: CustomerGroupEnum | '' = data.customerGroup || '';

    this.venuesByGroup = !customerGroup
      ? this.venues
      : this.venuesByGroup.filter((venue: Venue) => venue.customerGroup === customerGroup);

    await this.onVenuesChange(data);
  }

  public async saveScreensaver() {
    this.$refs.form.validate().then(async (res: boolean[] | boolean) => {
      if (this.isValid(res)) {
        const data: Partial<Screensaver> = { ...this.$refs.form.getData(), ...this.$refs.upsellForm.getData() };

        if (this.editing) {
          await this.updateScreensaver({ id: this.id, ...data });
          await this.backToTable();
        } else {
          const screensaver = await this.storeScreensaver(data);
          await this.$router.push({ name: 'screensaver.edit', params: { id: screensaver.data._id } });
          this.activeTab = 1;
        }
      }
    });
  }
}
